import { errorMessage } from "./errorMessage";
import {
  isEmpty,
  invalidChars,
  invalidDigits,
  validateDEA,
  validAge,
  invalidFax,
  isTrueValue,
  lengthChar,
} from "./rules";

const validateSlots = practiceDetails => {
  let isOverlapping = false;
  let isTimeGreater = false;
  let noSlotFound = false;
  let weekDaysInvalid = false;
  let acceptUntilInvalid = false;
  let slotsEmpty = false;
  let slotDataEmpty = false;
  let dateGreater = false;

  practiceDetails.calendar.forEach(cal => {
    if (isEmpty(cal.startDate) || isEmpty(cal.endDate)) {
      acceptUntilInvalid = true;
    } else if (cal.startDate > cal.endDate) {
      dateGreater = true;
    } else if (isEmpty(cal.workingDays)) {
      weekDaysInvalid = true;
    } else if (
      isEmpty(cal.morningSlots) &&
      isEmpty(cal.eveningSlots) &&
      isEmpty(cal.afternoonSlots)
    ) {
      slotsEmpty = true;
    }

    const slots = [
      ...cal.morningSlots,
      ...cal.afternoonSlots,
      ...cal.eveningSlots,
    ];

    const slts = [];

    slots.forEach(slot => {
      if (slot.startTime && slot.endTime && slot.duration) {
        const startDate = new Date();
        const endDate = new Date();

        startDate.setHours(slot.startTime.hour);
        startDate.setMinutes(slot.startTime.minutes);

        endDate.setHours(slot.endTime.hour);
        endDate.setMinutes(slot.endTime.minutes);

        slts.push({
          startDate,
          endDate,
          duration: slot.duration.value,
        });
      } else {
        slotDataEmpty = true;
      }
    });

    slts.sort(
      (startDate, endDate) =>
        startDate.startDate - endDate.startDate ||
        startDate.endDate - endDate.endDate
    );

    if (!slotDataEmpty) {
      slts.forEach((slot, i) => {
        const currStartTime = slot.startDate;
        const currEndTime = slot.endDate;

        if (currStartTime > currEndTime) {
          isTimeGreater = true;
        }

        const isSlotCreated =
          (currEndTime - currStartTime) / 60 / 1000 / slot.duration >= 1;

        if (!isSlotCreated) {
          noSlotFound = true;
        }

        if (i > 0) {
          const prevStartTime = slts[i - 1].startDate;
          const prevEndTime = slts[i - 1].endDate;

          if (
            (prevStartTime <= currStartTime && prevEndTime > currStartTime) ||
            (prevStartTime <= currEndTime && prevEndTime > currEndTime)
          ) {
            isOverlapping = true;
          }
        }
      });
    }
  });
  if (acceptUntilInvalid) {
    return errorMessage.invalidAcceptUntil;
  } else if (dateGreater) {
    return errorMessage.invalidDocStartDate;
  } else if (weekDaysInvalid) {
    return errorMessage.invalidWeekDays;
  } else if (slotsEmpty) {
    return errorMessage.slotsEmpty;
  } else if (slotDataEmpty) {
    return errorMessage.slotsDataEmpty;
  } else if (isTimeGreater) {
    return errorMessage.slotTimeMismatch;
  } else if (noSlotFound) {
    return errorMessage.noSlots;
  } else if (isOverlapping) {
    return errorMessage.slotTimeOverlapped;
  } else {
    return false;
  }
};

const validateDocuments = documents => {
  const message =
    isEmpty(documents.licenseStartDate, "License Start Date") ||
    isEmpty(documents.licenseEndDate, "License End Date") ||
    isEmpty(documents.deaStartDate, "DEA Start Date") ||
    isEmpty(documents.deaEndDate, "DEA End Date") ||
    isEmpty(documents.insuranceStartDate, "Insurance Start Date") ||
    isEmpty(documents.insuranceEndDate, "Insurance End Date") ||
    (documents.licenseStartDate > documents.licenseEndDate &&
      errorMessage.invalidDocStartDate) ||
    (documents.deaStartDate > documents.deaEndDate &&
      errorMessage.invalidDocStartDate) ||
    (documents.insuranceStartDate > documents.insuranceEndDate &&
      errorMessage.invalidDocStartDate);

  return message;
};

export const validatePersonalInfo = personalInfo => {
  const invalid = {
    firstName: isEmpty(personalInfo.firstName, "First Name"),
    lastName: isEmpty(personalInfo.lastName, "Last Name"),
    specialities: isEmpty(personalInfo.specialities, "Speciality"),
    gender: isEmpty(personalInfo.gender, "Gender"),
    date_of_birth:
      isEmpty(personalInfo.date_of_birth, "Date of Birth") ||
      validAge(personalInfo.date_of_birth),
    yearsOfExperience: isEmpty(
      personalInfo.yearsOfExperience,
      "Years of Experience"
    ),
    aboutMe:
      personalInfo.aboutMe && personalInfo.aboutMe.length > 0
        ? personalInfo.aboutMe.length > 500
          ? "Maximum limit is 500 characters."
          : false
        : false,
    languages: isEmpty(personalInfo.languages, "Languages"),
    degree: isEmpty(personalInfo.degree, "Degree"),
  };

  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;
  if (valid) {
    invalid.personalInfoValid = true;
  } else {
    invalid.personalInfoValid = false;
  }
  return invalid;
};

export const validatePracticeDetails = practiceDetails => {
  const fee =
    practiceDetails?.fees &&
    parseFloat(
      practiceDetails?.fees.toString().replace("$", "").replace(",", "")
    );

  const invalid = {
    practiceName:
      isEmpty(practiceDetails.practiceName, "Practice Name") ||
      invalidChars(practiceDetails.practiceName, "practice name"),
    fees:
      isEmpty(practiceDetails.fees, "Fees") || parseInt(fee) <= 0
        ? errorMessage.invalidFees
        : false,
    primaryPhone:
      isEmpty(practiceDetails.primaryPhone, "Primary Phone") ||
      invalidDigits(practiceDetails.primaryPhone, "Primary Phone", 10, 10),
    primaryPhoneType: isEmpty(
      practiceDetails.primaryPhoneType,
      "Primary Phone Type"
    ),
    primaryFax:
      isEmpty(practiceDetails.primaryFax, "Primary Fax") ||
      invalidFax(practiceDetails.primaryFax),
    address1:
      isEmpty(practiceDetails.address1, "Address") ||
      lengthChar(practiceDetails.address1),

    zipcode: isEmpty(practiceDetails.zipcode, "Zipcode"),
    city: isEmpty(practiceDetails.city, "City"),
    state: isEmpty(practiceDetails.state, "State"),
    country: isEmpty(practiceDetails.country, "Country"),
  };
  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;
  if (valid) {
    invalid.practiceDetailsValid = true;
  } else {
    invalid.practiceDetailsValid = false;
  }
  return invalid;
};

export const validateDocumentsDetail = documents => {
  const invalid = {
    npiNumber:
      isEmpty(documents.npiNumber, "NPI Number") ||
      invalidDigits(documents.npiNumber, "NPI Number", 10, 10),
    licenseNumber:
      isEmpty(documents.licenseNumber, "License Number") ||
      invalidDigits(documents.licenseNumber, "License Number", 5, 6),
    licenseState: isEmpty(documents.licenseState, "License State"),
    deaNumber:
      isEmpty(documents.deaNumber, "DEA Number") ||
      validateDEA(documents.deaNumber),
    deaState: isEmpty(documents.deaState, "DEA State"),
    insuranceCompanyName:
      isEmpty(documents.insuranceCompanyName, "Insurance Company Name") ||
      invalidChars(documents.insuranceCompanyName, "company name"),
    insuranceState: isEmpty(documents.insuranceState, "State"),
    documents: validateDocuments(documents),
  };
  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;
  if (valid) {
    invalid.documentsValid = true;
  } else {
    invalid.documentsValid = false;
  }
  return invalid;
};

export const validateSignatureDetail = sign => {
  const invalid = {
    signatureImage: isEmpty(sign.docsignature, "Signature"),
  };

  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;

  if (valid) {
    invalid.signatureValid = true;
  } else {
    invalid.signatureValid = false;
  }
  return invalid;
};

export const validateSubscription = sub => {
  const invalid = {
    subscriptionExist: isTrueValue(sub, "Subscription"),
  };

  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;

  if (valid) {
    invalid.isSubscriptionExist = true;
  } else {
    invalid.isSubscriptionExist = false;
  }
  console.log(invalid, "invalid");
  return invalid;
};

export const validateBankAccountDetail = accountDetails => {
  const invalid = {
    accountNumber:
      isEmpty(accountDetails.ach_account_number, "Account Number") ||
      invalidDigits(
        accountDetails.ach_account_number,
        "Account Number",
        12,
        12
      ),
    routingNumber:
      isEmpty(accountDetails.ach_routing_number, "Routing Number") ||
      invalidDigits(accountDetails.ach_routing_number, "Routing Number", 9, 9),
    accountHolderName:
      isEmpty(accountDetails.ach_account_holder_name, "Account Holder Name") ||
      invalidChars(
        accountDetails.ach_account_holder_name,
        "account holder name"
      ),
  };

  const valid =
    Object.keys(invalid).filter(el => invalid[el] && invalid[el].length > 0)
      .length === 0;
  if (valid) {
    invalid.accountValid = true;
  } else {
    invalid.accountValid = false;
  }
  return invalid;
};
